import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Info, XCircle } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '../lib/cn';
import { springGentle } from '../tokens';

export type ToastKind = 'success' | 'error' | 'info' | 'warning';

export type ToastOptions = {
  message: string;
  kind?: ToastKind;
  /** Auto-dismiss duration in ms. Defaults to 3000. */
  duration?: number;
};

type ToastEntry = ToastOptions & { id: number };

type ToastContextValue = {
  show: (opts: ToastOptions) => void;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}

const kindIcon: Record<ToastKind, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
};

const kindIconClass: Record<ToastKind, string> = {
  success: 'text-positive',
  error: 'text-negative',
  warning: 'text-warning',
  info: 'text-accent',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (opts: ToastOptions) => {
      const id = ++idRef.current;
      const entry: ToastEntry = { id, kind: 'info', duration: 3000, ...opts };
      setToasts((prev) => [...prev, entry]);
      const timer = setTimeout(() => dismiss(id), entry.duration);
      timersRef.current.set(id, timer);
    },
    [dismiss],
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastEntry[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div
      aria-live="polite"
      className={cn(
        'fixed inset-x-0 top-0 z-[60] flex flex-col items-center px-screen-x',
        'pt-[max(env(safe-area-inset-top),12px)] pointer-events-none',
      )}
    >
      <AnimatePresence>
        {toasts.map((t) => {
          const kind = t.kind ?? 'info';
          const Icon = kindIcon[kind];
          return (
            <motion.button
              type="button"
              key={t.id}
              onClick={() => onDismiss(t.id)}
              initial={{ opacity: 0, y: -16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={springGentle}
              className={cn(
                'pointer-events-auto mt-2 w-full max-w-content',
                'inline-flex items-center gap-3 px-4 py-3',
                'rounded-button bg-surface shadow-lg text-left',
              )}
            >
              <Icon
                size={20}
                strokeWidth={1.5}
                className={cn('shrink-0', kindIconClass[kind])}
              />
              <span className="text-[14px] text-text-primary flex-1 truncate">
                {t.message}
              </span>
            </motion.button>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
